import {
    BarChart3,
    Heart,
    AlertTriangle,
    Search,
    Database,
    MessageSquare,
    TrendingUp,
    PieChart as PieChartIcon,
    ArrowUp,
    ArrowDown
} from 'lucide-react';
import { PieChart, BarChart, LineChart, PiecewiseColorLegend } from '@mui/x-charts';
import StatCard from './ui/StatCard';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import ApiService from '../services/ApiService';
import BarLabelAtBase from './BarLabelAtBase';
import BarShadedBackground from './BarShadedBackground';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import ChartFooter from './ui/ChartFooter';
import { DESCRIPTIONS } from '../utils/descriptions';
import InfoTooltip from './ui/InfoTooltip';
import { Paper, Typography } from '@mui/material';
import QueryPreviewModal from './ui/QueryPreviewModal';

// --- Utility Components & Functions ---

// Consistent Color Palette
const CHART_COLORS = [
    '#000000', // Black
    '#333333', // Dark Gray
    '#666666', // Gray
    '#999999', // Light Gray
    '#CCCCCC', // Lighter Gray
    '#E5E5E5', // Very Light Gray
    '#F5F5F5', // Off White
    '#111111',
    '#222222',
    '#444444',
];

// Sort Button Component
const SortButton = ({ sortOrder, onSort }) => {
    return (
        <button
            onClick={onSort}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-black bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors ml-auto"
            title={`Sort ${sortOrder === 'desc' ? 'ascending' : 'descending'}`}
        >
            {sortOrder === 'desc' ? (
                <>
                    <ArrowDown className="h-3.5 w-3.5" />
                    <span>High to Low</span>
                </>
            ) : (
                <>
                    <ArrowUp className="h-3.5 w-3.5" />
                    <span>Low to High</span>
                </>
            )}
        </button>
    );
};

// Helper to sort chart data
const sortChartData = (data, sortOrder) => {
    if (!data) return [];
    return [...data].sort((a, b) => {
        if (sortOrder === 'desc') {
            return b.value - a.value;
        }
        return a.value - b.value;
    });
};

// Empty State Component
const EmptyChart = ({ icon: Icon, title, description }) => (
    <div className="flex flex-col items-center justify-center h-64 text-center px-4">
        <div className="p-4 bg-gray-100 rounded-full mb-4">
            <Icon className="h-8 w-8 text-black" />
        </div>
        <h4 className="text-lg font-semibold text-gray-900 mb-2">{title}</h4>
        <p className="text-sm text-gray-500 max-w-sm">{description}</p>
    </div>
);

const getEmotionEmoji = (emotion) => {
    const emotionEmojis = {
        // Positive emotions
        'happy': '😊', 'excited': '🎉', 'joy': '😄', 'love': '❤️', 'satisfied': '😌',
        'grateful': '🙏', 'appreciation': '🙏', 'proud': '😎', 'relief': '😮‍💨',
        'calm': '😌', 'content': '☺️', 'enthusiastic': '🤩', 'optimistic': '😃',
        'hopeful': '🌟', 'amused': '😄', 'delighted': '😁',
        // Negative emotions
        'angry': '😠', 'frustrated': '😤', 'sad': '😢', 'disappointed': '😞',
        'upset': '😔', 'annoyed': '😒', 'irritated': '😑', 'furious': '🤬',
        'hurt': '💔', 'depressed': '😭', 'miserable': '😣', 'bitter': '😖',
        'resentful': '😾',
        // Anxiety/Fear related
        'anxious': '😰', 'anxiety': '😰', 'worried': '😟', 'nervous': '😬',
        'stressed': '😓', 'overwhelmed': '😵', 'fearful': '😨', 'scared': '😱',
        'panicked': '😰', 'tense': '😬',
        // Confusion/Uncertainty
        'confused': '😕', 'uncertain': '🤔', 'doubtful': '🤨', 'skeptical': '🤨',
        'puzzled': '😵‍💫', 'perplexed': '😵', 'bewildered': '😳',
        // Neutral
        'neutral': '😐', 'indifferent': '😶', 'bored': '😑', 'tired': '😴',
        // Surprise
        'surprised': '😲', 'shocked': '😱', 'amazed': '😮', 'astonished': '🤯',
        // Urgency/Action
        'urgent': '⚡', 'impatient': '⏰', 'rushed': '🏃',
        // Other
        'curious': '🤔', 'interested': '👀', 'disgusted': '🤢', 'embarrassed': '😳',
        'shame': '😳', 'guilty': '😔', 'lonely': '😔', 'jealous': '😒', 'envious': '😤'
    };
    const normalizedEmotion = emotion.toLowerCase().trim();
    return emotionEmojis[normalizedEmotion] || '💭';
};

const generateRootCauseInsights = (analytics) => {
    const insights = [];
    const { category_distribution, top_root_causes } = analytics;

    // 1. Dominant Category Insight
    if (category_distribution && category_distribution.length > 0) {
        const topCategory = category_distribution[0];
        const total = category_distribution.reduce((acc, curr) => acc + curr.count, 0);
        const percentage = Math.round((topCategory.count / total) * 100);
        
        insights.push({
            type: 'primary',
            text: `**${topCategory.root_cause_category.replace(/_/g, ' ').toUpperCase()}** is the primary driver, accounting for **${percentage}%** of all analyzed queries.`
        });
    }

    // 2. Specific Issue Insight (using top_root_causes if available)
    if (top_root_causes && top_root_causes.length > 0) {
        // Sort by confidence or frequency if available, taking the top one
        const topIssue = top_root_causes[0];
        insights.push({
            type: 'specific',
            text: `A significant number of users are reporting: "**${topIssue.specific_issue}**".`
        });

        // 3. Evidence/Context Insight
        if (topIssue.evidence && topIssue.evidence.length > 0) {
             insights.push({
                type: 'context',
                text: `Key user feedback includes: "${topIssue.evidence[0]}"`
            });
        }
    }

    // Fallback if no specific issues data
    if (insights.length === 1) {
         insights.push({
            type: 'general',
            text: "Reviewing the top categories can help prioritize support resources effectively."
        });
    }

    return insights;
};

const generateQueryClassificationInsights = (analytics) => {
    const insights = [];
    const { primary_category_distribution, intent_distribution } = analytics;

    if (primary_category_distribution && primary_category_distribution.length > 0) {
        const topCategory = primary_category_distribution[0];
        const total = primary_category_distribution.reduce((acc, curr) => acc + curr.count, 0);
        const percentage = Math.round((topCategory.count / total) * 100);
        
        insights.push({
            type: 'primary',
            text: `**${topCategory.primary_category.replace(/_/g, ' ').toUpperCase()}** is the most common query type (**${percentage}%**).`
        });

        if (topCategory.primary_category.toLowerCase().includes('support')) {
             insights.push({
                type: 'action',
                text: "Action: Review support documentation to cover common FAQs and reduce ticket volume."
            });
        } else if (topCategory.primary_category.toLowerCase().includes('sales')) {
             insights.push({
                type: 'action',
                text: "Action: Optimize sales funnels or AI prompts to better convert these leads."
            });
        }
    }

    if (intent_distribution && intent_distribution.length > 0) {
        const topIntent = intent_distribution[0];
        insights.push({
            type: 'specific',
            text: `Most users have an intent of **${topIntent.intent.toUpperCase()}**.`
        });
    }

    return insights;
};

const generateRagPerformanceInsights = (analytics) => {
    const insights = [];
    const { average_rag_performance_score, knowledge_gap_category_distribution } = analytics;

    insights.push({
        type: 'primary',
        text: `The average RAG performance score is **${Math.round(average_rag_performance_score)}%**.`
    });

    if (average_rag_performance_score < 70) {
        insights.push({
            type: 'action',
            text: "Action: The score is below target. Urgent review of knowledge base quality is required."
        });
    }

    if (knowledge_gap_category_distribution && knowledge_gap_category_distribution.length > 0) {
        const topGap = knowledge_gap_category_distribution[0];
        insights.push({
            type: 'specific',
            text: `Top Knowledge Gap: **${topGap.category.toUpperCase()}**.`
        });
        
        insights.push({
            type: 'action',
            text: `Action: Add more comprehensive content related to **${topGap.category}** to the knowledge base.`
        });
    }

    return insights;
};

const generateUrgencyInsights = (analytics) => {
    const insights = [];
    const { urgency_level_distribution, average_urgency_score } = analytics;

    insights.push({
        type: 'primary',
        text: `The average urgency score is **${Math.round(average_urgency_score)}/100**.`
    });

    const highUrgency = urgency_level_distribution?.find(u => u.urgency_level === 'high' || u.urgency_level === 'critical');
    if (highUrgency && highUrgency.count > 0) {
        const total = urgency_level_distribution.reduce((acc, curr) => acc + curr.count, 0);
        const percentage = Math.round((highUrgency.count / total) * 100);
        
        insights.push({
            type: 'specific',
            text: `**${percentage}%** of issues are flagged as **High/Critical Urgency**.`
        });

        insights.push({
            type: 'action',
            text: "Action: Allocate immediate resources to address these critical blockers to prevent churn."
        });
    } else {
        insights.push({
            type: 'specific',
            text: "Most issues are low urgency, indicating a stable support environment."
        });
    }

    return insights;
};

// --- Main Component ---

const RenderDetailView = ({ selectedReport, hasFilter, params = {} }) => {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalQueries, setModalQueries] = useState([]);
    const [modalTitle, setModalTitle] = useState('');
    const [modalFilterType, setModalFilterType] = useState('');
    const [modalFilterValue, setModalFilterValue] = useState('');
    const [modalLoading, setModalLoading] = useState(false);
    const [modalPage, setModalPage] = useState(1);
    const [modalPagination, setModalPagination] = useState({
        current_page: 1,
        total_pages: 1,
        total_items: 0,
        has_next: false,
        has_previous: false
    });

    // State to manage sort order for various charts
    const [sortStates, setSortStates] = useState({});

    // Helper to toggle sort state for a specific key
    const handleSort = (key) => {
        setSortStates(prev => ({
            ...prev,
            [key]: (prev[key] || 'desc') === 'desc' ? 'asc' : 'desc'
        }));
    };

    // Helper to get current sort order or default to 'desc'
    const getSort = (key) => sortStates[key] || 'desc';

    const fetchModalQueries = async (page, filterType, filterValue) => {
        setModalLoading(true);
        setModalPage(page);
        
        try {
            const payload = {
                page: page,
                per_page: 10,
                filters: {}
            };

            if (params.start_date && params.end_date) {
                payload.start_date = params.start_date;
                payload.end_date = params.end_date;
            }

            // Map filter types to payload structure
            if (filterType === 'root_cause_category') {
                payload.filters.root_cause = { category: [filterValue] };
            } else if (filterType === 'primary_category') {
                payload.filters.classification = { primary_category: [filterValue] };
            } else if (filterType === 'intent') {
                // Intent from API might be capitalized, ensuring we send what backend expects
                // Usually backend expects lowercase with underscores or specific format
                // Trying to infer from context or just sending as is if it matches
                payload.filters.classification = { intent: [filterValue] };
            } else if (filterType === 'urgency_level') {
                payload.filters.urgency = { level: [filterValue.toLowerCase()] };
            } else if (filterType === 'urgency_impact') {
                payload.filters.urgency = { business_impact: [filterValue.toLowerCase()] };
            } else if (filterType === 'sentiment_emotion') {
                payload.filters.sentiment = { emotion: [filterValue] };
            } else if (filterType === 'sentiment_overall') {
                payload.filters.sentiment = { overall: [filterValue.toLowerCase()] };
            } else if (filterType === 'context_quality') {
                payload.filters.rag_performance = { context_quality: [filterValue] };
            } else if (filterType === 'language') {
                payload.filters.query_technicals = { language: [filterValue.toLowerCase()] };
            } else if (filterType === 'complexity') {
                payload.filters.query_technicals = { query_complexity: [filterValue.toLowerCase()] };
            }

            const response = await ApiService.getSupportQueries(payload);
            
            let queriesData = [];
            if (response.success && response.data) {
                queriesData = response.data;
            } else if (Array.isArray(response)) {
                queriesData = response;
            } else if (response.queries) {
                queriesData = response.queries;
            }

            if (response.pagination) {
                setModalPagination(response.pagination);
            }

            if (queriesData && queriesData.length > 0) {
                setModalQueries(queriesData);
            } else {
                setModalQueries([]);
                if (page === 1) toast.info('No queries found for this filter');
            }
        } catch (error) {
            console.error('Failed to fetch queries:', error);
            toast.error('Failed to load queries');
            setModalQueries([]);
        } finally {
            setModalLoading(false);
        }
    };

    const handleModalPageChange = (newPage) => {
        fetchModalQueries(newPage, modalFilterType, modalFilterValue);
    };

    const handleChartClick = (filterType, filterValue, title) => {
        setModalOpen(true);
        setModalTitle(title);
        setModalFilterType(filterType);
        setModalFilterValue(filterValue);
        setModalQueries([]);
        setModalPage(1);
        fetchModalQueries(1, filterType, filterValue);
    };

    // Custom Tooltip for Charts
    const CustomChartTooltip = (props) => {
        const { itemData, series } = props;
        if (!itemData) return null;

        let label = '';
        let value = '';
        let id = '';

        // Handle PieChart
        if (series.data) {
             const dataItem = series.data[itemData.dataIndex];
             if (dataItem) {
                id = dataItem.id;
                label = dataItem.label ? dataItem.label.split('|')[0].trim() : '';
                value = dataItem.value;
             }
        }
        
        let description = '';
        if (id) {
            if (DESCRIPTIONS.primary_category[id]) description = DESCRIPTIONS.primary_category[id];
            else if (DESCRIPTIONS.intent[id]) description = DESCRIPTIONS.intent[id];
            else if (DESCRIPTIONS.root_cause[id]) description = DESCRIPTIONS.root_cause[id];
            else if (DESCRIPTIONS.rag_performance[id]) description = DESCRIPTIONS.rag_performance[id];
        }

        if (!label && !value) return null;

        return (
            <Paper elevation={3} sx={{ p: 2, maxWidth: 300, bgcolor: 'rgba(255, 255, 255, 0.95)' }}>
                <Typography variant="subtitle2" color="primary" fontWeight="bold">
                    {label}
                </Typography>
                <Typography variant="body2" sx={{ my: 1 }}>
                    Count: <strong>{value}</strong>
                </Typography>
                {description && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1, borderTop: 1, borderColor: 'divider', pt: 1, fontStyle: 'italic' }}>
                        {description}
                    </Typography>
                )}
            </Paper>
        );
    };

    const callApi = async (params = {}) => {
        try {
            setLoading(true);

            if (selectedReport === 'aiResponseQuality') {
                const response = await ApiService.aiResponseQuality(params);
                if (response.success && response.analytics) {
                    const { completeness, grounding, clarity } = response.analytics;
                    const transformScoreDistribution = (scoreData) => {
                        if (!scoreData?.score_distribution?.data) return [];
                        return scoreData.score_distribution.data.map(item => ({
                            label: `${item.range[0]}-${item.range[1]}`,
                            value: item.count
                        }));
                    };
                    setReportData({
                        completeness: completeness?.avg_score || 0,
                        grounding: grounding?.avg_score || 0,
                        clarity: clarity?.avg_score || 0,
                        completenessDistribution: transformScoreDistribution(completeness),
                        groundingDistribution: transformScoreDistribution(grounding),
                        clarityDistribution: transformScoreDistribution(clarity),
                        dateRange: response.analytics.date_range
                    });
                }
            } else if (selectedReport === 'queryClassification') {
                const response = await ApiService.queryClassification(params);
                if (response.success && response.analytics) {
                    const { primary_category_distribution, intent_distribution } = response.analytics;
                    const topCategory = primary_category_distribution?.reduce((max, item) => item.count > max.count ? item : max, { count: 0 });
                    const topIntent = intent_distribution?.reduce((max, item) => item.count > max.count ? item : max, { count: 0 });
                    
                    const insights = generateQueryClassificationInsights(response.analytics);

                    setReportData({
                        primaryCategory: primary_category_distribution?.map(item => ({
                            id: item.primary_category,
                            label: item.primary_category.replace(/_/g, ' ').toUpperCase(),
                            value: item.count
                        })) || [],
                        intent: intent_distribution?.map(item => ({
                            label: item.intent.replace(/_/g, ' ').toUpperCase(),
                            value: item.count
                        })) || [],
                        totalQueries: primary_category_distribution?.reduce((sum, item) => sum + item.count, 0) || 0,
                        topCategory: topCategory?.primary_category.replace(/_/g, ' ').toUpperCase() || 'N/A',
                        topIntent: topIntent?.intent.replace(/_/g, ' ').toUpperCase() || 'N/A',
                        insights,
                        dateRange: response.analytics.date_range
                    });
                }
            } else if (selectedReport === 'queryTechnicals') {
                const response = await ApiService.queryTechnicals(params);
                if (response.success && response.analytics) {
                    const { query_complexity_distribution, language_distribution, character_length, word_count, average_sentence_count, question_mark_count, url_reference_count } = response.analytics;
                    setReportData({
                        complexity: query_complexity_distribution?.map(item => ({
                            id: item.query_complexity,
                            label: item.query_complexity.toUpperCase(),
                            value: item.count
                        })) || [],
                        language: language_distribution?.map(item => ({
                            id: item.language,
                            label: item.language.toUpperCase(),
                            value: item.count
                        })) || [],
                        characterLength: character_length?.average || 0,
                        characterDistribution: character_length?.distribution?.data?.map(item => ({
                            label: `${item.range[0]}-${item.range[1]}`,
                            value: item.count
                        })) || [],
                        wordCount: word_count?.average || 0,
                        wordDistribution: word_count?.distribution?.data?.map(item => ({
                            label: `${item.range[0]}-${item.range[1]}`,
                            value: item.count
                        })) || [],
                        avgSentenceCount: average_sentence_count || 0,
                        questionMarkCount: question_mark_count || 0,
                        urlReferenceCount: url_reference_count || 0,
                        totalQueries: query_complexity_distribution?.reduce((sum, item) => sum + item.count, 0) || 0,
                        dateRange: response.analytics.date_range
                    });
                }
            } else if (selectedReport === 'sentiment') {
                const [sentimentResponse, timelineResponse] = await Promise.all([
                    ApiService.sentiment(params),
                    ApiService.sentimentOverTime(params)
                ]);

                if (sentimentResponse.success && sentimentResponse.analytics) {
                    const { overall_sentiment, emotion_distribution, average_sentiment_score, sentiment_score_distribution } = sentimentResponse.analytics;
                    const emotionData = emotion_distribution.map(d => ({
                        label: `${getEmotionEmoji(d.emotion)} ${d.emotion.charAt(0).toUpperCase() + d.emotion.slice(1)}`,
                        value: d.count,
                        emotion: d.emotion
                    }));
                    const topCause = emotion_distribution?.reduce((max, item) => item.count > max.count ? item : max, { count: 0 });
                    
                    const timelineData = timelineResponse.success ? timelineResponse.analytics.sentiment_over_time : [];

                    setReportData({
                        overall: overall_sentiment?.map(item => ({
                            id: item.sentiment,
                            label: item.sentiment.toUpperCase(),
                            value: item.count
                        })) || [],
                        emotion: emotionData || [],
                        topEmotion: topCause,
                        averageScore: average_sentiment_score || 0,
                        scoreDistribution: sentiment_score_distribution?.data?.map(item => ({
                            label: `${item.range[0]} to ${item.range[1]}`,
                            value: item.count
                        })) || [],
                        timeline: timelineData,
                        positiveRate: overall_sentiment?.find(s => s.sentiment === 'positive')?.count || 0,
                        negativeRate: overall_sentiment?.find(s => s.sentiment === 'negative')?.count || 0,
                        neutralRate: overall_sentiment?.find(s => s.sentiment === 'neutral')?.count || 0,
                        totalCount: overall_sentiment?.reduce((sum, item) => sum + item.count, 0) || 0,
                        dateRange: sentimentResponse.analytics.date_range
                    });
                }
            } else if (selectedReport === 'urgency') {
                const response = await ApiService.urgency(params);
                if (response.success && response.analytics) {
                    const { urgency_level_distribution, business_impact_distribution, average_urgency_score, urgency_score_distribution } = response.analytics;
                    setReportData({
                        level: urgency_level_distribution?.map(item => ({
                            id: item.urgency_level,
                            label: item.urgency_level.toUpperCase(),
                            value: item.count
                        })) || [],
                        businessImpact: business_impact_distribution?.map(item => ({
                            label: item.business_impact.toUpperCase(),
                            value: item.count
                        })) || [],
                        averageScore: average_urgency_score || 0,
                        scoreDistribution: urgency_score_distribution?.data?.map(item => ({
                            label: `${item.range[0]}-${item.range[1]}`,
                            value: item.count
                        })) || [],
                        highUrgency: urgency_level_distribution?.find(u => u.urgency_level === 'high')?.count || 0,
                        criticalCount: urgency_level_distribution?.find(u => u.urgency_level === 'critical')?.count || 0,
                        totalCount: urgency_level_distribution?.reduce((sum, item) => sum + item.count, 0) || 0,
                        insights: generateUrgencyInsights(response.analytics),
                        dateRange: response.analytics.date_range
                    });
                }
            } else if (selectedReport === 'rootCause') {
                const response = await ApiService.rootCause(params);
                if (response.success && response.analytics) {
                    const { category_distribution, root_cause_identification_avg_confidence } = response.analytics;
                    
                    const insights = generateRootCauseInsights(response.analytics);
                    const topCause = category_distribution?.reduce((max, item) => item.count > max.count ? item : max, { count: 0 });
                    
                    setReportData({
                        category: category_distribution?.map(item => ({
                            id: item.root_cause_category,
                            label: item.root_cause_category.replace(/_/g, ' ').toUpperCase(),
                            value: item.count
                        })) || [],
                        avgConfidence: Math.round(root_cause_identification_avg_confidence * 100) || 0,
                        totalCauses: category_distribution?.length || 0,
                        topCause: topCause?.root_cause_category.replace(/_/g, ' ').toUpperCase() || 'N/A',
                        topCausePercentage: category_distribution?.length > 0
                            ? Math.round((category_distribution[0].count / category_distribution.reduce((sum, item) => sum + item.count, 0)) * 100)
                            : 0,
                        insights, // Store generated insights
                        dateRange: response.analytics.date_range
                    });
                }
            } else if (selectedReport === 'ragPerformance') {
                const response = await ApiService.ragPerformance(params);
                if (response.success && response.analytics) {
                    const { context_quality_distribution, knowledge_gap_category_distribution, average_rag_performance_score, rag_performance_score_distribution } = response.analytics;
                    setReportData({
                        contextQuality: context_quality_distribution?.map(item => ({
                            id: item.context_quality,
                            label: item.context_quality.replace(/_/g, ' ').toUpperCase(),
                            value: item.count
                        })) || [],
                        knowledgeGaps: knowledge_gap_category_distribution?.map(item => ({
                            label: item.category.replace(/_/g, ' ').toUpperCase(),
                            value: item.count
                        })) || [],
                        averageScore: average_rag_performance_score || 0,
                        scoreDistribution: rag_performance_score_distribution?.data?.map(item => ({
                            label: `${item.range[0]}-${item.range[1]}`,
                            value: item.count
                        })) || [],
                        excellentQuality: context_quality_distribution?.find(c => c.context_quality.includes('high') || c.context_quality.includes('excellent'))?.count || 0,
                        totalCount: context_quality_distribution?.reduce((sum, item) => sum + item.count, 0) || 0,
                        insights: generateRagPerformanceInsights(response.analytics),
                        dateRange: response.analytics.date_range
                    });
                }
            }
            setLoading(false);
        } catch (error) {
            console.log('API error:', error);
            toast.error('Failed to load data');
            setLoading(false);
        }
    }

    useEffect(() => {
        if (hasFilter && Object.keys(params).length > 0) {
            callApi(params);
        } else {
            callApi();
        }
    }, [selectedReport, hasFilter, params])


    if (loading) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Skeleton variant="rectangular" height={350} className="rounded-xl" />
                    <Skeleton variant="rectangular" height={350} className="rounded-xl" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Skeleton variant="rectangular" height={100} className="rounded-xl" />
                    <Skeleton variant="rectangular" height={100} className="rounded-xl" />
                    <Skeleton variant="rectangular" height={100} className="rounded-xl" />
                    <Skeleton variant="rectangular" height={100} className="rounded-xl" />
                </div>
            </div>
        );
    }

    if (!reportData) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <p className="text-gray-500">No data available</p>
                </div>
            </div>
        );
    }

    const generateUniqueColors = (count) => {
        const colors = [];
        const usedColors = new Set();
        while (colors.length < count) {
            const color = '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
            if (!usedColors.has(color)) {
                colors.push(color);
                usedColors.add(color);
            }
        }
        return colors;
    };

    const renderContent = () => {
        switch (selectedReport) {
            case 'queryClassification':
                return (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                            <div className="flex justify-between items-center mb-4">
                                <div className="flex items-center">
                                    <h3 className="text-base font-semibold text-gray-900">Primary Category Distribution</h3>
                                    <InfoTooltip text="Distribution of queries based on their primary topic or domain." />
                                </div>
                                <SortButton sortOrder={getSort('primaryCategory')} onSort={() => handleSort('primaryCategory')} />
                            </div>
                            {reportData.primaryCategory?.length > 0 ? (
                                <PieChart
                                    series={[{
                                        data: sortChartData(reportData.primaryCategory, getSort('primaryCategory')).map(d => ({
                                            ...d,
                                            label: `${d.label} | ${((d.value / reportData.primaryCategory.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(0)}% | ${d.value} Queries` || 'N/A'
                                        })),
                                        highlightScope: { faded: 'global', highlighted: 'item' },
                                    }]}
                                    colors={CHART_COLORS}
                                    height={300}
                                    tooltip={{ trigger: 'item' }}
                                    slots={{ itemContent: CustomChartTooltip }}
                                    slotProps={{
                                        legend: {
                                            direction: 'row',
                                            position: { vertical: 'bottom', horizontal: 'middle' },
                                            padding: 0,
                                        },
                                    }}
                                    onItemClick={(event, d) => {
                                        const sortedData = sortChartData(reportData.primaryCategory, getSort('primaryCategory'));
                                        const clickedItem = sortedData[d.dataIndex];
                                        handleChartClick(
                                            'primary_category',
                                            clickedItem.id,
                                            `Category: ${clickedItem.label.split('|')[0].trim()}`
                                        );
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                />
                            ) : (
                                <EmptyChart icon={PieChartIcon} title="No Data" description="Category data will appear here" />
                            )}
                            <ChartFooter dateRange={reportData.dateRange} />
                        </div>

                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                            <div className="flex justify-between items-center mb-4">
                                <div className="flex items-center">
                                    <h3 className="text-base font-semibold text-gray-900">Intent Distribution</h3>
                                    <InfoTooltip text="Breakdown of what the user is trying to achieve with their query." />
                                </div>
                                <SortButton sortOrder={getSort('intent')} onSort={() => handleSort('intent')} />
                            </div>
                            {reportData.intent?.length > 0 ? (
                                <Stack>
                                    <BarChart
                                        height={400}
                                        dataset={sortChartData(reportData.intent, getSort('intent'))}
                                        series={[{
                                            id: 'turnout',
                                            dataKey: 'value',
                                            color: CHART_COLORS[2], // amber-500
                                        }]}
                                        layout="horizontal"
                                        barLabel={(v) => {
                                            const sorted = sortChartData(reportData.intent, getSort('intent'));
                                            const item = sorted[v.dataIndex];
                                            return item ? `${item.label}: ${v.value}` : '';
                                        }}
                                        yAxis={[{
                                            scaleType: 'band',
                                            dataKey: 'label',
                                            width: 140,
                                        }]}
                                        slots={{
                                            legend: PiecewiseColorLegend,
                                            barLabel: BarLabelAtBase,
                                            bar: BarShadedBackground,
                                        }}
                                        onItemClick={(event, d) => {
                                            const sortedData = sortChartData(reportData.intent, getSort('intent'));
                                            if (d && typeof d.dataIndex !== 'undefined') {
                                                const clickedItem = sortedData[d.dataIndex];
                                                // Use label as ID, converting to format expected by filter
                                                const rawIntent = clickedItem.label.toLowerCase().replace(/ /g, '_');
                                                handleChartClick('intent', rawIntent, `Intent: ${clickedItem.label}`);
                                            }
                                        }}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                </Stack>
                            ) : (
                                <EmptyChart icon={BarChart3} title="No Data" description="Intent data will appear here" />
                            )}
                            <ChartFooter dateRange={reportData.dateRange} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard label="Total Queries" value={reportData.totalQueries?.toString() || '0'} color="blue" />
                        <StatCard label="Total Categories" value={reportData.primaryCategory?.length?.toString() || '0'} color="green" />
                        <StatCard label="Top Category" value={reportData.topCategory || 'N/A'} color="purple" />
                        <StatCard label="Top Intent" value={reportData.topIntent || 'N/A'} color="cyan" />
                    </div>

                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                        <h4 className="text-base font-bold text-black mb-4 flex items-center gap-2">
                            <Search className="w-4 h-4" />
                            Key Insights
                        </h4>
                        <div className="space-y-3">
                            {reportData.insights && reportData.insights.map((insight, idx) => (
                                <div key={idx} className="flex gap-3 bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                    <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                                        insight.type === 'primary' ? 'bg-black' : 
                                        insight.type === 'specific' ? 'bg-gray-600' : 
                                        insight.type === 'action' ? 'bg-gray-400' : 'bg-gray-300'
                                    }`} />
                                    <div className="text-sm text-black leading-relaxed">
                                        <span dangerouslySetInnerHTML={{ 
                                            __html: insight.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                                        }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            );

        case 'queryTechnicals':
            return (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-base font-semibold text-gray-900">Query Complexity Distribution</h3>
                                <SortButton sortOrder={getSort('complexity')} onSort={() => handleSort('complexity')} />
                            </div>
                            {reportData.complexity?.length > 0 ? (
                                <PieChart
                                    series={[{
                                        data: sortChartData(reportData.complexity, getSort('complexity')).map(d => ({
                                            ...d,
                                            label: `${d.label} | ${((d.value / reportData.complexity.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(0)}% | ${d.value} Queries` || 'N/A'
                                        })),
                                        highlightScope: { faded: 'global', highlighted: 'item' },
                                    }]}
                                    colors={CHART_COLORS}
                                    height={300}
                                    onItemClick={(event, d) => {
                                        const sortedData = sortChartData(reportData.complexity, getSort('complexity'));
                                        const clickedItem = sortedData[d.dataIndex];
                                        handleChartClick(
                                            'complexity',
                                            clickedItem.id,
                                            `Complexity: ${clickedItem.label.split('|')[0].trim()}`
                                        );
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                />
                            ) : (
                                <EmptyChart icon={PieChartIcon} title="No Data" description="Complexity data will appear here" />
                            )}
                            <ChartFooter dateRange={reportData.dateRange} />
                        </div>

                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-base font-semibold text-gray-900">Language Distribution</h3>
                                <SortButton sortOrder={getSort('language')} onSort={() => handleSort('language')} />
                            </div>
                            {reportData.language?.length > 0 ? (
                                <PieChart
                                    series={[{
                                        data: sortChartData(reportData.language, getSort('language')).map(d => ({
                                            ...d,
                                            label: `${d.label} | ${((d.value / reportData.language.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(0)}% | ${d.value} Queries` || 'N/A'
                                        })),
                                        highlightScope: { faded: 'global', highlighted: 'item' },
                                    }]}
                                    colors={CHART_COLORS}
                                    height={300}
                                    onItemClick={(event, d) => {
                                        const sortedData = sortChartData(reportData.language, getSort('language'));
                                        const clickedItem = sortedData[d.dataIndex];
                                        handleChartClick(
                                            'language',
                                            clickedItem.id,
                                            `Language: ${clickedItem.label.split('|')[0].trim()}`
                                        );
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                />
                            ) : (
                                <EmptyChart icon={PieChartIcon} title="No Data" description="Language data will appear here" />
                            )}
                            <ChartFooter dateRange={reportData.dateRange} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-base font-semibold text-gray-900">Character Length Distribution</h3>
                                <SortButton sortOrder={getSort('charLength')} onSort={() => handleSort('charLength')} />
                            </div>
                            {reportData.characterDistribution?.length > 0 ? (
                                <BarChart
                                    xAxis={[{
                                        scaleType: 'band',
                                        data: sortChartData(reportData.characterDistribution, getSort('charLength')).map(d => d.label)
                                    }]}
                                    series={[{
                                        data: sortChartData(reportData.characterDistribution, getSort('charLength')).map(d => d.value),
                                        color: CHART_COLORS[4], // violet-500
                                    }]}
                                    height={300}
                                />
                            ) : (
                                <EmptyChart icon={BarChart3} title="No Data" description="Character length data will appear here" />
                            )}
                            <ChartFooter dateRange={reportData.dateRange} />
                        </div>

                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-base font-semibold text-gray-900">Word Count Distribution</h3>
                                <SortButton sortOrder={getSort('wordCount')} onSort={() => handleSort('wordCount')} />
                            </div>
                            {reportData.wordDistribution?.length > 0 ? (
                                <BarChart
                                    xAxis={[{
                                        scaleType: 'band',
                                        data: sortChartData(reportData.wordDistribution, getSort('wordCount')).map(d => d.label)
                                    }]}
                                    series={[{
                                        data: sortChartData(reportData.wordDistribution, getSort('wordCount')).map(d => d.value),
                                        color: CHART_COLORS[5], // cyan-500
                                    }]}
                                    height={300}
                                />
                            ) : (
                                <EmptyChart icon={BarChart3} title="No Data" description="Word count data will appear here" />
                            )}
                            <ChartFooter dateRange={reportData.dateRange} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard label="Avg Character Length" value={reportData.characterLength?.toString() || '0'} color="purple" />
                        <StatCard label="Avg Word Count" value={reportData.wordCount?.toString() || '0'} color="cyan" />
                        <StatCard label="Avg Sentences" value={reportData.avgSentenceCount?.toString() || '0'} color="blue" />
                        <StatCard label="Total Queries" value={reportData.totalQueries?.toString() || '0'} color="green" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-black mb-1">Question Marks</p>
                                    <p className="text-3xl font-bold text-black">{reportData.questionMarkCount}</p>
                                </div>
                                <MessageSquare className="h-10 w-10 text-black" />
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-black mb-1">URL References</p>
                                    <p className="text-3xl font-bold text-black">{reportData.urlReferenceCount}</p>
                                </div>
                                <Database className="h-10 w-10 text-black" />
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-black mb-1">Total Queries</p>
                                    <p className="text-3xl font-bold text-black">{reportData.totalQueries}</p>
                                </div>
                                <BarChart3 className="h-10 w-10 text-black" />
                            </div>
                        </div>
                    </div>
                </div>
            );

        case 'sentiment':
            const totalSentiment = reportData.totalCount || 1;
            const positivePercent = Math.round((reportData.positiveRate / totalSentiment) * 100);
            const negativePercent = Math.round((reportData.negativeRate / totalSentiment) * 100);
            const neutralPercent = Math.round((reportData.neutralRate / totalSentiment) * 100);

            return (
                <div className="space-y-6">
                    {/* Sentiment Trend Line Chart */}
                    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-base font-semibold text-gray-900">Sentiment Over Time (Last 7 Days)</h3>
                        </div>
                        {reportData.timeline?.length > 0 ? (
                            <LineChart
                                xAxis={[{
                                    scaleType: 'point',
                                    data: reportData.timeline.map(d => d.date),
                                }]}
                                series={[{
                                    data: reportData.timeline.map(d => d.score),
                                    color: CHART_COLORS[0],
                                    label: 'Avg Sentiment Score',
                                    area: true,
                                }]}
                                height={300}
                                grid={{ vertical: true, horizontal: true }}
                            />
                        ) : (
                            <EmptyChart icon={TrendingUp} title="No Timeline Data" description="Trend data will appear here" />
                        )}
                        <ChartFooter dateRange={reportData.dateRange} />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-base font-semibold text-gray-900">Overall Sentiment</h3>
                                <SortButton sortOrder={getSort('sentiment')} onSort={() => handleSort('sentiment')} />
                            </div>
                            {reportData.overall?.length > 0 ? (
                                <PieChart
                                    series={[{
                                        data: sortChartData(reportData.overall, getSort('sentiment')).map(d => ({
                                            ...d,
                                            label: `${d.label} | ${((d.value / reportData.overall.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(0)}%`,
                                        })),
                                        highlightScope: { faded: 'global', highlighted: 'item' },
                                    }]}
                                    colors={CHART_COLORS}
                                    height={300}
                                    slotProps={{
                                        legend: {
                                            direction: 'row',
                                            position: { vertical: 'bottom', horizontal: 'middle' },
                                            padding: 0,
                                        },
                                    }}
                                    onItemClick={(event, d) => {
                                        const sortedData = sortChartData(reportData.overall, getSort('sentiment'));
                                        const clickedItem = sortedData[d.dataIndex];
                                        handleChartClick(
                                            'sentiment_overall',
                                            clickedItem.id,
                                            `Sentiment: ${clickedItem.label.split('|')[0].trim()}`
                                        );
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                />
                            ) : (
                                <EmptyChart icon={PieChartIcon} title="No Data" description="Sentiment data will appear here" />
                            )}
                            <ChartFooter dateRange={reportData.dateRange} />
                        </div>

                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-base font-semibold text-gray-900">Sentiment Score Distribution</h3>
                                <SortButton sortOrder={getSort('sentimentScore')} onSort={() => handleSort('sentimentScore')} />
                            </div>
                            {reportData.scoreDistribution?.length > 0 ? (
                                <BarChart
                                    xAxis={[{
                                        scaleType: 'band',
                                        data: sortChartData(reportData.scoreDistribution, getSort('sentimentScore')).map(d => d.label)
                                    }]}
                                    series={[{
                                        data: sortChartData(reportData.scoreDistribution, getSort('sentimentScore')).map(d => d.value),
                                        color: CHART_COLORS[1], // emerald-500
                                    }]}
                                    height={300}
                                />
                            ) : (
                                <EmptyChart icon={TrendingUp} title="No Data" description="Score distribution will appear here" />
                            )}
                            <ChartFooter dateRange={reportData.dateRange} />
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-semibold text-gray-900">Emotion Analysis</h3>
                            <SortButton sortOrder={getSort('emotion')} onSort={() => handleSort('emotion')} />
                        </div>
                        {reportData.emotion?.length > 0 ? (
                            <div className="space-y-4">
                                {/* Summary stats */}
                                <div className="grid grid-cols-3 gap-4 mb-6">
                                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                        <p className="text-xs font-medium text-black mb-1">Total Emotions</p>
                                        <p className="text-2xl font-bold text-black">
                                            {reportData.emotion.reduce((sum, d) => sum + d.value, 0)}
                                        </p>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                        <p className="text-xs font-medium text-black mb-1">Most Common</p>
                                        <p className="text-lg font-bold text-black flex items-center gap-2">
                                            {getEmotionEmoji(reportData.topEmotion?.emotion)}
                                            <span className="capitalize">{reportData.topEmotion?.emotion}</span>
                                        </p>
                                    </div>
                                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                        <p className="text-xs font-medium text-black mb-1">Categories</p>
                                        <p className="text-2xl font-bold text-black">
                                            {reportData.emotion.length}
                                        </p>
                                    </div>
                                </div>

                                <Stack>
                                    <BarChart
                                        height={400}
                                        dataset={sortChartData(reportData.emotion, getSort('emotion'))}
                                        series={[{
                                            id: 'turnout',
                                            dataKey: 'value',
                                            color: CHART_COLORS[1], // emerald-500
                                        }]}
                                        layout="horizontal"
                                        barLabel={(v) => {
                                            const sorted = sortChartData(reportData.emotion, getSort('emotion'));
                                            return `${sorted[v.dataIndex].label}: ${v.value}`;
                                        }}
                                        yAxis={[{
                                            scaleType: 'band',
                                            dataKey: 'label',
                                            width: 130,
                                        }]}
                                        slots={{
                                            legend: PiecewiseColorLegend,
                                            barLabel: BarLabelAtBase,
                                            bar: BarShadedBackground,
                                        }}
                                        onItemClick={(event, d) => {
                                            const sortedData = sortChartData(reportData.emotion, getSort('emotion'));
                                            if (d && typeof d.dataIndex !== 'undefined') {
                                                const clickedItem = sortedData[d.dataIndex];
                                                handleChartClick(
                                                    'sentiment_emotion',
                                                    clickedItem.emotion, // Use raw emotion
                                                    `Emotion: ${clickedItem.emotion}`
                                                );
                                            }
                                        }}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                </Stack>
                            </div>
                        ) : (
                            <EmptyChart icon={BarChart3} title="No Emotion Data" description="Emotion analysis will appear here" />
                        )}
                        <ChartFooter dateRange={reportData.dateRange} />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard label="Positive Rate" value={`${positivePercent}%`} color="green" />
                        <StatCard label="Avg Sentiment" value={reportData.averageScore?.toFixed(2) || '0'} color="blue" />
                        <StatCard label="Negative Rate" value={`${negativePercent}%`} color="red" />
                        <StatCard label="Neutral Rate" value={`${neutralPercent}%`} color="orange" />
                    </div>
                </div>
            );

        case 'urgency':
            const totalUrgency = reportData.totalCount || 1;
            const highPercent = Math.round((reportData.highUrgency / totalUrgency) * 100);
            const criticalPercent = Math.round((reportData.criticalCount / totalUrgency) * 100);

            return (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-base font-semibold text-gray-900">Urgency Level Distribution</h3>
                                <SortButton sortOrder={getSort('urgencyLevel')} onSort={() => handleSort('urgencyLevel')} />
                            </div>
                            {reportData.level?.length > 0 ? (
                                <PieChart
                                    series={[{
                                        data: sortChartData(reportData.level, getSort('urgencyLevel')).map(d => ({
                                            ...d,
                                            label: `${d.label} | ${((d.value / reportData.level.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(0)}% | ${d.value} Queries` || 'N/A'
                                        })),
                                        highlightScope: { faded: 'global', highlighted: 'item' },
                                    }]}
                                    colors={CHART_COLORS}
                                    height={300}
                                    onItemClick={(event, d) => {
                                        const sortedData = sortChartData(reportData.level, getSort('urgencyLevel'));
                                        const clickedItem = sortedData[d.dataIndex];
                                        handleChartClick(
                                            'urgency_level',
                                            clickedItem.id,
                                            `Urgency: ${clickedItem.label.split('|')[0].trim()}`
                                        );
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                />
                            ) : (
                                <EmptyChart icon={PieChartIcon} title="No Data" description="Urgency data will appear here" />
                            )}
                            <ChartFooter dateRange={reportData.dateRange} />
                        </div>

                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-base font-semibold text-gray-900">Business Impact</h3>
                                <SortButton sortOrder={getSort('impact')} onSort={() => handleSort('impact')} />
                            </div>
                            {reportData.businessImpact?.length > 0 ? (
                                <Stack>
                                    <BarChart
                                        height={300}
                                        dataset={sortChartData(reportData.businessImpact, getSort('impact'))}
                                        series={[{
                                            id: 'turnout',
                                            dataKey: 'value',
                                            color: CHART_COLORS[9] // orange-500
                                        }]}
                                        layout="horizontal"
                                        barLabel={(v) => {
                                            const sorted = sortChartData(reportData.businessImpact, getSort('impact'));
                                            const item = sorted[v.dataIndex];
                                            return item ? `${item.label}: ${v.value}` : '';
                                        }}
                                        yAxis={[{
                                            scaleType: 'band',
                                            dataKey: 'label',
                                            width: 140,
                                        }]}
                                        slots={{
                                            legend: PiecewiseColorLegend,
                                            barLabel: BarLabelAtBase,
                                            bar: BarShadedBackground,
                                        }}
                                        onItemClick={(event, d) => {
                                            const sortedData = sortChartData(reportData.businessImpact, getSort('impact'));
                                            if (d && typeof d.dataIndex !== 'undefined') {
                                                const clickedItem = sortedData[d.dataIndex];
                                                handleChartClick(
                                                    'urgency_impact',
                                                    clickedItem.label.toLowerCase(),
                                                    `Impact: ${clickedItem.label}`
                                                );
                                            }
                                        }}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                </Stack>
                            ) : (
                                <EmptyChart icon={BarChart3} title="No Data" description="Impact data will appear here" />
                            )}
                            <ChartFooter dateRange={reportData.dateRange} />
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-base font-semibold text-gray-900">Urgency Score Distribution (0-100)</h3>
                            <SortButton sortOrder={getSort('urgencyScore')} onSort={() => handleSort('urgencyScore')} />
                        </div>
                        {reportData.scoreDistribution?.length > 0 ? (
                            <BarChart
                                xAxis={[{
                                    scaleType: 'band',
                                    data: sortChartData(reportData.scoreDistribution, getSort('urgencyScore')).map(d => d.label)
                                }]}
                                series={[{
                                    data: sortChartData(reportData.scoreDistribution, getSort('urgencyScore')).map(d => d.value),
                                    color: CHART_COLORS[9], // orange-500
                                }]}
                                height={300}
                            />
                        ) : (
                            <EmptyChart icon={TrendingUp} title="No Data" description="Score distribution will appear here" />
                        )}
                        <ChartFooter dateRange={reportData.dateRange} />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard label="High Urgency" value={`${highPercent}%`} color="red" />
                        <StatCard label="Avg Score" value={reportData.averageScore?.toString() || '0'} color="orange" />
                        <StatCard label="Critical Issues" value={`${criticalPercent}%`} color="purple" />
                        <StatCard label="Total Queries" value={reportData.totalCount?.toString() || '0'} color="cyan" />
                    </div>

                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                        <h4 className="text-base font-bold text-black mb-4 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            Urgency Insights
                        </h4>
                        <div className="space-y-3">
                            {reportData.insights && reportData.insights.map((insight, idx) => (
                                <div key={idx} className="flex gap-3 bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                    <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                                        insight.type === 'primary' ? 'bg-black' : 
                                        insight.type === 'specific' ? 'bg-gray-600' : 
                                        insight.type === 'action' ? 'bg-gray-400' : 'bg-gray-300'
                                    }`} />
                                    <div className="text-sm text-black leading-relaxed">
                                        <span dangerouslySetInnerHTML={{ 
                                            __html: insight.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                                        }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            );

        case 'rootCause':
            return (
                <div className="space-y-6">
                    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                        <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center">
                                <h3 className="text-base font-semibold text-gray-900">Root Cause Categories</h3>
                                <InfoTooltip text="Underlying reasons for the user's issue or inquiry." />
                            </div>
                            <SortButton sortOrder={getSort('rootCause')} onSort={() => handleSort('rootCause')} />
                        </div>
                        {reportData.category?.length > 0 ? (
                            <PieChart
                                series={[{
                                    data: sortChartData(reportData.category, getSort('rootCause')).map(d => ({
                                        ...d,
                                        label: `${d.label} | ${((d.value / reportData.category.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(0)}% | ${d.value} Queries` || 'N/A'
                                    })),
                                    highlightScope: { faded: 'global', highlighted: 'item' },
                                }]}
                                    colors={CHART_COLORS}
                                    height={350}
                                    tooltip={{ trigger: 'item' }}
                                    slots={{ itemContent: CustomChartTooltip }}
                                    onItemClick={(event, d) => {
                                        const sortedData = sortChartData(reportData.category, getSort('rootCause'));
                                        const clickedItem = sortedData[d.dataIndex];
                                        handleChartClick(
                                            'root_cause_category',
                                            clickedItem.id,
                                            `Root Cause: ${clickedItem.label.split('|')[0].trim()}`
                                        );
                                    }}
                                    sx={{ cursor: 'pointer' }}
                            />
                        ) : (
                            <EmptyChart icon={BarChart3} title="No Data" description="Root cause data will appear here" />
                        )}
                        <ChartFooter dateRange={reportData.dateRange} />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard label="Identified Causes" value={reportData.totalCauses?.toString() || '0'} color="purple" />
                        <StatCard label="Avg Confidence" value={`${reportData.avgConfidence}%`} color="cyan" />
                        <StatCard label="Top Cause" value={reportData.topCause || 'N/A'} color="red" />
                        <StatCard label="Top Cause %" value={`${reportData.topCausePercentage}%`} color="green" />
                    </div>

                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                        <h4 className="text-base font-bold text-black mb-4 flex items-center gap-2">
                            <Search className="w-4 h-4" />
                            Key Insights
                        </h4>
                        <div className="space-y-3">
                            {reportData.insights && reportData.insights.map((insight, idx) => (
                                <div key={idx} className="flex gap-3 bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                    <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                                        insight.type === 'primary' ? 'bg-black' : 
                                        insight.type === 'specific' ? 'bg-gray-600' : 'bg-gray-400'
                                    }`} />
                                    <div className="text-sm text-black leading-relaxed">
                                        <span dangerouslySetInnerHTML={{ 
                                            __html: insight.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                                        }} />
                                    </div>
                                </div>
                            ))}
                            {!reportData.insights && (
                                <p className="text-sm text-purple-800 italic">No specific insights available for this period.</p>
                            )}
                        </div>
                    </div>
                </div>
            );

        case 'ragPerformance':
            const excellentPercent = reportData.totalCount > 0
                ? Math.round((reportData.excellentQuality / reportData.totalCount) * 100)
                : 0;

            return (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-base font-semibold text-gray-900">Context Quality Distribution</h3>
                                <SortButton sortOrder={getSort('contextQuality')} onSort={() => handleSort('contextQuality')} />
                            </div>
                            {reportData.contextQuality?.length > 0 ? (
                                <PieChart
                                    series={[{
                                        data: sortChartData(reportData.contextQuality, getSort('contextQuality')).map(d => ({
                                            ...d,
                                            label: `${d.label} | ${((d.value / reportData.contextQuality.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(0)}% | ${d.value} Queries` || 'N/A'
                                        })),
                                        highlightScope: { faded: 'global', highlighted: 'item' },
                                    }]}
                                    colors={CHART_COLORS}
                                    height={300}
                                    onItemClick={(event, d) => {
                                        const sortedData = sortChartData(reportData.contextQuality, getSort('contextQuality'));
                                        const clickedItem = sortedData[d.dataIndex];
                                        handleChartClick(
                                            'context_quality',
                                            clickedItem.id,
                                            `Context Quality: ${clickedItem.label.split('|')[0].trim()}`
                                        );
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                />
                            ) : (
                                <EmptyChart icon={PieChartIcon} title="No Data" description="Quality data will appear here" />
                            )}
                            <ChartFooter dateRange={reportData.dateRange} />
                        </div>

                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                            <div className="flex justify-between items-center mb-4">
                                <div className="flex items-center">
                                    <h3 className="text-base font-semibold text-gray-900">Knowledge Gap Categories</h3>
                                    <InfoTooltip text="Areas where the AI lacked sufficient information to answer." />
                                </div>
                                <SortButton sortOrder={getSort('knowledgeGaps')} onSort={() => handleSort('knowledgeGaps')} />
                            </div>
                            {reportData.knowledgeGaps?.length > 0 ? (
                                <Stack>
                                    <BarChart
                                        height={300}
                                        dataset={sortChartData(reportData.knowledgeGaps, getSort('knowledgeGaps'))}
                                        series={[{
                                            id: 'turnout',
                                            dataKey: 'value',
                                            color: CHART_COLORS[5] // cyan-500
                                        }]}
                                        layout="horizontal"
                                        barLabel={(v) => {
                                            const sorted = sortChartData(reportData.knowledgeGaps, getSort('knowledgeGaps'));
                                            const item = sorted[v.dataIndex];
                                            return item ? `${item.label}: ${v.value}` : '';
                                        }}
                                        yAxis={[{
                                            scaleType: 'band',
                                            dataKey: 'label',
                                            width: 50,
                                        }]}
                                        slots={{
                                            legend: PiecewiseColorLegend,
                                            barLabel: BarLabelAtBase,
                                            bar: BarShadedBackground,
                                        }}
                                    />
                                </Stack>
                            ) : (
                                <EmptyChart icon={BarChart3} title="No Data" description="Gap data will appear here" />
                            )}
                            <ChartFooter dateRange={reportData.dateRange} />
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-base font-semibold text-gray-900">RAG Performance Score Distribution (0-100)</h3>
                            <SortButton sortOrder={getSort('ragScore')} onSort={() => handleSort('ragScore')} />
                        </div>
                        {reportData.scoreDistribution?.length > 0 ? (
                            <BarChart
                                xAxis={[{
                                    scaleType: 'band',
                                    data: sortChartData(reportData.scoreDistribution, getSort('ragScore')).map(d => d.label)
                                }]}
                                series={[{
                                    data: sortChartData(reportData.scoreDistribution, getSort('ragScore')).map(d => d.value),
                                    color: CHART_COLORS[5], // cyan-500
                                }]}
                                height={300}
                            />
                        ) : (
                            <EmptyChart icon={TrendingUp} title="No Data" description="Score distribution will appear here" />
                        )}
                        <ChartFooter dateRange={reportData.dateRange} />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard label="Excellent Quality" value={`${excellentPercent}%`} color="green" />
                        <StatCard label="Avg RAG Score" value={reportData.averageScore?.toString() || '0'} color="cyan" />
                        <StatCard label="Total Queries" value={reportData.totalCount?.toString() || '0'} color="orange" />
                        <StatCard label="Gap Categories" value={reportData.knowledgeGaps?.length?.toString() || '0'} color="blue" />
                    </div>

                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                        <h4 className="text-base font-bold text-black mb-4 flex items-center gap-2">
                            <Database className="w-4 h-4" />
                            RAG Performance Recommendations
                        </h4>
                        <div className="space-y-3">
                            {reportData.insights && reportData.insights.map((insight, idx) => (
                                <div key={idx} className="flex gap-3 bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                    <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                                        insight.type === 'primary' ? 'bg-black' : 
                                        insight.type === 'specific' ? 'bg-gray-600' : 
                                        insight.type === 'action' ? 'bg-gray-400' : 'bg-gray-300'
                                    }`} />
                                    <div className="text-sm text-black leading-relaxed">
                                        <span dangerouslySetInnerHTML={{ 
                                            __html: insight.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                                        }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            );

        case 'aiResponseQuality':
            return (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-base font-semibold text-gray-900">Completeness Distribution</h3>
                                <SortButton sortOrder={getSort('completeness')} onSort={() => handleSort('completeness')} />
                            </div>
                            {reportData.completenessDistribution?.length > 0 ? (
                                <BarChart
                                    xAxis={[{
                                        scaleType: 'band',
                                        data: sortChartData(reportData.completenessDistribution, getSort('completeness')).map(d => d.label)
                                    }]}
                                    series={[{
                                        data: sortChartData(reportData.completenessDistribution, getSort('completeness')).map(d => d.value),
                                        color: CHART_COLORS[0]
                                    }]}
                                    height={250}
                                />
                            ) : (
                                <EmptyChart icon={BarChart3} title="No Data" description="Completeness data will appear here" />
                            )}
                            <ChartFooter dateRange={reportData.dateRange} />
                        </div>

                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-base font-semibold text-gray-900">Grounding Distribution</h3>
                                <SortButton sortOrder={getSort('grounding')} onSort={() => handleSort('grounding')} />
                            </div>
                            {reportData.groundingDistribution?.length > 0 ? (
                                <BarChart
                                    xAxis={[{
                                        scaleType: 'band',
                                        data: sortChartData(reportData.groundingDistribution, getSort('grounding')).map(d => d.label)
                                    }]}
                                    series={[{
                                        data: sortChartData(reportData.groundingDistribution, getSort('grounding')).map(d => d.value),
                                        color: CHART_COLORS[1]
                                    }]}
                                    height={250}
                                />
                            ) : (
                                <EmptyChart icon={BarChart3} title="No Data" description="Grounding data will appear here" />
                            )}
                            <ChartFooter dateRange={reportData.dateRange} />
                        </div>

                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-base font-semibold text-gray-900">Clarity Distribution</h3>
                                <SortButton sortOrder={getSort('clarity')} onSort={() => handleSort('clarity')} />
                            </div>
                            {reportData.clarityDistribution?.length > 0 ? (
                                <BarChart
                                    xAxis={[{
                                        scaleType: 'band',
                                        data: sortChartData(reportData.clarityDistribution, getSort('clarity')).map(d => d.label)
                                    }]}
                                    series={[{
                                        data: sortChartData(reportData.clarityDistribution, getSort('clarity')).map(d => d.value),
                                        color: CHART_COLORS[2]
                                    }]}
                                    height={250}
                                />
                            ) : (
                                <EmptyChart icon={BarChart3} title="No Data" description="Clarity data will appear here" />
                            )}
                            <ChartFooter dateRange={reportData.dateRange} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-black">Completeness</span>
                            </div>
                            <div className="text-3xl font-bold text-black mb-3">{reportData.completeness}%</div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div className="bg-black h-2 rounded-full" style={{ width: `${reportData.completeness}%` }}></div>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-black">Grounding</span>
                            </div>
                            <div className="text-3xl font-bold text-black mb-3">{reportData.grounding}%</div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div className="bg-black h-2 rounded-full" style={{ width: `${reportData.grounding}%` }}></div>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-black">Clarity</span>
                            </div>
                            <div className="text-3xl font-bold text-black mb-3">{reportData.clarity}%</div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div className="bg-black h-2 rounded-full" style={{ width: `${reportData.clarity}%` }}></div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard
                            label="Overall Quality"
                            value={`${Math.round((reportData.completeness + reportData.grounding + reportData.clarity) / 3)}%`}
                            color="blue"
                        />
                        <StatCard
                            label="Completeness"
                            value={`${reportData.completeness}%`}
                            color="purple"
                        />
                        <StatCard
                            label="Grounding"
                            value={`${reportData.grounding}%`}
                            color="green"
                        />
                        <StatCard
                            label="Clarity"
                            value={`${reportData.clarity}%`}
                            color="orange"
                        />
                    </div>
                </div>
            );

        default:
            return null;
    }
    };

    return (
        <>
            {renderContent()}
            <QueryPreviewModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                queries={modalQueries}
                title={modalTitle}
                filterType={modalFilterType}
                filterValue={modalFilterValue}
                loading={modalLoading}
                pagination={modalPagination}
                onPageChange={handleModalPageChange}
            />
        </>
    );
};

export default RenderDetailView;
